import type { Contact, CrmCategory, Opportunity } from '@client-outreach/shared';
import { contactRelevance, normalizeCompanyName, opportunityTimingStage, routeRole } from '@client-outreach/shared';
import type { ProspectSearchResult } from '../data/api-client';

export function Icon({name,className=''}:{name:string;className?:string}){
  return <span className={`material-symbols-outlined uiIcon ${className}`.trim()} aria-hidden="true">{name}</span>;
}

export function InfoHint({text,label='More information',className=''}:{text:string;label?:string;className?:string}){
  return <span className={`infoHint ${className}`.trim()} tabIndex={0} data-tooltip={text} aria-label={`${label}: ${text}`}><Icon name="info"/></span>;
}


const GENERIC_EMAIL_DOMAINS=new Set(['gmail.com','googlemail.com','outlook.com','hotmail.com','live.com','icloud.com','me.com','yahoo.com','yahoo.co.uk','aol.com','proton.me','protonmail.com']);
export function companyWebsiteFromEmail(email?:string){
  const domain=String(email||'').trim().toLowerCase().split('@')[1]||'';
  if(!domain||GENERIC_EMAIL_DOMAINS.has(domain)||!domain.includes('.'))return undefined;
  return `https://${domain}`;
}

function domainFromWebsite(value?:string){
  if(!value)return '';
  try{
    const raw=/^https?:\/\//i.test(value)?value:`https://${value}`;
    return new URL(raw).hostname.replace(/^www\./,'');
  }catch{return value.replace(/^https?:\/\//i,'').split('/')[0].replace(/^www\./,'');}
}


export function companyWebsiteHref(value?:string){
  const raw=String(value||'').trim();
  if(!raw)return undefined;
  return /^https?:\/\//i.test(raw)?raw:`https://${raw}`;
}

export function CompanyLink({name,website,className=''}:{name:string;website?:string;className?:string}){
  const href=companyWebsiteHref(website);
  if(!href)return <span className={className}>{name}</span>;
  return <a className={`companyNameLink ${className}`.trim()} href={href} target="_blank" rel="noreferrer" onClick={event=>event.stopPropagation()} title={`Open ${name} website`}>{name}</a>;
}

export function CompanyLogo({name,website,size='md'}:{name:string;website?:string;size?:'sm'|'md'|'lg'}){
  const domain=domainFromWebsite(website);
  const initials=(name||'?').split(/\s+/).filter(Boolean).slice(0,2).map(x=>x[0]).join('').toUpperCase();
  return <span className={`companyLogo ${size}`} title={name}>
    {domain?<img loading="lazy" decoding="async" src={`https://www.google.com/s2/favicons?sz=128&domain_url=${encodeURIComponent(`https://${domain}`)}`} alt="" onError={e=>{(e.currentTarget as HTMLImageElement).style.display='none';}}/>:null}
    <span>{initials||'?'}</span>
  </span>;
}

export function effectiveContactCategory(contact:Contact):CrmCategory{
  const routed=routeRole(contact.title);
  return contact.crmCategory==='Misc'&&routed.confident&&routed.crmCategory!=='Misc'?routed.crmCategory:contact.crmCategory;
}

export function categoryForOpportunity(opportunity:Opportunity):CrmCategory{
  const text=[opportunity.type,opportunity.name,opportunity.recommendedAngle,...(opportunity.serviceTags||[])].join(' ').toLowerCase();
  if(/partnership|sponsor|commercial/.test(text))return 'Partnerships';
  if(/experiential|experience|event|fan|hospitality|activation/.test(text))return 'Events & Experiential';
  if(/creative|design|motion|3d|cgi|visual/.test(text))return 'Creative';
  if(/marketing|campaign|brand|social|content/.test(text))return 'Marketing';
  return 'Misc';
}

export type OpportunityRadarGroup='Upcoming Events'|'New Partnerships'|'Live Briefs & Tenders'|'Account Triggers'|'Planning Ahead'|'Other Opportunities';
export const opportunityRadarGroups:OpportunityRadarGroup[]=['Upcoming Events','New Partnerships','Live Briefs & Tenders','Account Triggers','Planning Ahead','Other Opportunities'];

export function radarGroupForOpportunity(opportunity:Opportunity,now=Date.now()):OpportunityRadarGroup{
  const stage=opportunityTimingStage(opportunity,now);
  if(opportunity.type==='new-partnership')return stage==='too-late'?'Other Opportunities':'New Partnerships';
  if(opportunity.type==='live-brief')return 'Live Briefs & Tenders';
  if(opportunity.type==='account-trigger')return 'Account Triggers';
  if(opportunity.type==='future-event'){
    if(stage==='too-late')return 'Other Opportunities';
    if(stage==='contact-now'||stage==='opening-soon')return 'Upcoming Events';
    return 'Planning Ahead';
  }
  return 'Other Opportunities';
}

export function contactFit(contact:Contact,opportunities:Opportunity[]=[]):number{
  let score=contactRelevance(contact.title);
  const linked=opportunities.some(o=>o.companyId===contact.companyId||String(o.companyName||'').toLowerCase()===contact.companyName.toLowerCase());
  if(linked)score+=10;
  if(contact.emailVerification==='verified')score+=7;
  else if(contact.email)score+=3;
  if(contact.contactedAt)score-=10;
  return Math.max(0,Math.min(100,score));
}

export function prospectFit(person:ProspectSearchResult,opportunities:Opportunity[]=[]):number{
  let score=contactRelevance(person.title);
  const company=normalizeCompanyName(person.company);
  const linked=opportunities.some(o=>
    (o.companyName&&normalizeCompanyName(o.companyName)===company)||
    (o.targetCompanies||[]).some(target=>normalizeCompanyName(target.name)===company)
  );
  if(linked)score+=10;
  if(person.locationLabel)score+=2;
  if(person.timeZone)score+=3;
  return Math.max(0,Math.min(100,score));
}

export function ProgressRing({value:inputValue,label='%',title,ariaLabel,className=''}:{value:number;label?:string;title?:string;ariaLabel?:string;className?:string}){
  const value=Math.round(Math.max(0,Math.min(100,inputValue)));
  const tone=value>=70?'high':value>=40?'mid':'low';
  const accessible=ariaLabel||`${value}% ${label.replace(/^%\s*/, '').toLowerCase()}`.trim();
  return <span className={`progressRing ${tone} ${className}`.trim()} role="img" aria-label={accessible} title={title||accessible}>
    <svg viewBox="0 0 36 36" aria-hidden="true"><circle className="fitRingTrack progressRingTrack" cx="18" cy="18" r="15.5"/><circle className="fitRingProgress progressRingProgress" cx="18" cy="18" r="15.5" pathLength="100" strokeDasharray={`${value} 100`}/></svg>
    <b>{value}</b><small>{label}</small>
  </span>;
}

export function FitBadge({score,label='% FIT'}:{score:number;label?:string}){
  const value=Math.round(Math.max(0,Math.min(100,score)));
  return <ProgressRing value={value} label={label} className="fitBadge" ariaLabel={`${value}% fit`} title={`${value}% fit · Fit combines role relevance, opportunity relevance, service fit, relationship context and timing.`}/>;
}

export function CategoryTag({category}:{category:CrmCategory}){
  return <span className="categoryTag">{category}</span>;
}

export function RadarGroupTag({group}:{group:OpportunityRadarGroup}){
  return <span className="radarGroupTag">{group==='Upcoming Events'?'EVENT':group}</span>;
}

export function dateLabel(value?:string){
  if(!value)return 'Not set';
  const d=new Date(value);if(Number.isNaN(d.getTime()))return value;
  return d.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
}
